import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PAYMENT_ICONS } from '../constants/paymentIcons';
import ImageZoomViewer from '../components/ImageZoomViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ID_TYPE_LABELS = {
  'passport': 'Passport',
  'drivers_license': "Driver's License",
  'national_id': 'National ID (PhilSys ID)',
  'sss_id': 'SSS ID',
  'umid': 'UMID',
  'philhealth': 'PhilHealth ID',
  'voters_id': "Voter's ID",
  'postal_id': 'Postal ID',
  'prc_id': 'PRC ID'
};

const PAYMENT_METHOD_CONFIG = {
  'gcash': { label: 'GCash', icon: PAYMENT_ICONS.gcash },
  'maya': { label: 'Maya', icon: PAYMENT_ICONS.maya },
  'bank_account': { label: 'Bank Account', icon: PAYMENT_ICONS.bank_account }
};

const AdminIDVerification = ({ user, onLogout, embedded = false }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [viewerImage, setViewerImage] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/id-verifications?status=${activeTab}`),
        axios.get(`${API}/admin/id-verification-stats`)
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch verification data');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 5000);
  };

  const handleApprove = async (userId) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/id-verifications/${userId}/approve`);
      showSuccess('ID verification approved successfully!');
      fetchData();
      setShowModal(false);
      setSelectedUser(null);
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to approve verification');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (userId) => {
    if (!rejectReason.trim()) {
      showError('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/id-verifications/${userId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      showSuccess('ID verification rejected');
      fetchData();
      setShowModal(false);
      setSelectedUser(null);
      setRejectReason('');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to reject verification');
    } finally {
      setProcessing(false);
    }
  };

  const openUserDetails = (userDetails) => {
    setSelectedUser(userDetails);
    setShowModal(true);
    setRejectReason('');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      pending_admin_review: 'bg-amber-100 text-amber-800',
      needs_review: 'bg-amber-100 text-amber-800',
      verified: 'bg-green-100 text-green-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'Pending',
      pending_admin_review: 'Needs Review',
      needs_review: 'Needs Review',
      verified: 'Verified',
      approved: 'Verified',
      rejected: 'Rejected'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || (status?.charAt(0).toUpperCase() + status?.slice(1)) || 'Unknown'}
      </span>
    );
  };

  // Surface the AI's plain-language reason (if any) so admins can decide fast.
  const getAiReason = (u) => {
    const r = u?.kyc_verification_result;
    if (r && typeof r === 'object') {
      if (r.user_friendly_reason) return r.user_friendly_reason;
      if (r.recommendations) return r.recommendations;
    }
    if (Array.isArray(u?.kyc_rejection_reasons) && u.kyc_rejection_reasons.length > 0) {
      const first = u.kyc_rejection_reasons[0];
      return typeof first === 'string' ? first : (first?.reason || null);
    }
    return null;
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to view this page.</p>
            <Link to="/dashboard" className="mt-4 inline-block text-blue-600 hover:underline">
              Return to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'} data-testid="admin-id-verification-page">
      {!embedded && <Navbar user={user} onLogout={onLogout} />}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className={`text-3xl font-bold ${embedded ? 'text-white' : 'bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent'}`}>
              ID Verification Management
            </h1>
            <p className={embedded ? 'text-gray-300 mt-1' : 'text-gray-600 mt-1'}>Review and verify user identities</p>
          </div>
          {!embedded && (
            <Link
              to="/admin"
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              ← Back to Admin Dashboard
            </Link>
          )}
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" data-testid="success-message">
            ✓ {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-message">
            ✗ {error}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div 
              className={`bg-white rounded-lg shadow p-4 cursor-pointer border-2 transition ${activeTab === 'pending' ? 'border-yellow-500' : 'border-transparent hover:border-gray-200'}`}
              onClick={() => setActiveTab('pending')}
              data-testid="stat-pending"
            >
              <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
              <div className="text-gray-600 text-sm">Pending Review</div>
            </div>
            <div 
              className={`bg-white rounded-lg shadow p-4 cursor-pointer border-2 transition ${activeTab === 'verified' ? 'border-green-500' : 'border-transparent hover:border-gray-200'}`}
              onClick={() => setActiveTab('verified')}
              data-testid="stat-verified"
            >
              <div className="text-3xl font-bold text-green-600">{stats.verified}</div>
              <div className="text-gray-600 text-sm">Verified</div>
            </div>
            <div 
              className={`bg-white rounded-lg shadow p-4 cursor-pointer border-2 transition ${activeTab === 'rejected' ? 'border-red-500' : 'border-transparent hover:border-gray-200'}`}
              onClick={() => setActiveTab('rejected')}
              data-testid="stat-rejected"
            >
              <div className="text-3xl font-bold text-red-600">{stats.rejected}</div>
              <div className="text-gray-600 text-sm">Rejected</div>
            </div>
            <div 
              className={`bg-white rounded-lg shadow p-4 cursor-pointer border-2 transition ${activeTab === 'all' ? 'border-blue-500' : 'border-transparent hover:border-gray-200'}`}
              onClick={() => setActiveTab('all')}
              data-testid="stat-all"
            >
              <div className="text-3xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-gray-600 text-sm">Total Users</div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h2 className="text-lg font-semibold">
              {activeTab === 'pending' && '⏳ Pending Verification'}
              {activeTab === 'verified' && '✅ Verified Users'}
              {activeTab === 'rejected' && '❌ Rejected Users'}
              {activeTab === 'all' && '📋 All Users'}
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No users found in this category
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="font-medium text-gray-900">{u.name}</div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          u.role === 'provider' ? 'bg-blue-100 text-blue-800' : 
                          u.role === 'client' ? 'bg-purple-100 text-purple-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {ID_TYPE_LABELS[u.id_type] || u.id_type || 'Not provided'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {u.payment_method_type && PAYMENT_METHOD_CONFIG[u.payment_method_type] ? (
                          <span className="flex items-center gap-2">
                            <img src={PAYMENT_METHOD_CONFIG[u.payment_method_type].icon} alt="" className="w-6 h-6 object-contain rounded" />
                            {PAYMENT_METHOD_CONFIG[u.payment_method_type].label}
                          </span>
                        ) : (
                          u.payment_method_type || 'Not provided'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(u.id_verification_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openUserDetails(u)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                          data-testid={`view-details-${u.id}`}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* User Details Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="user-details-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-orange-500 to-orange-600 rounded-t-lg">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-white">User Verification Details</h3>
                <button
                  onClick={() => { setShowModal(false); setSelectedUser(null); }}
                  className="text-white hover:text-gray-200"
                  data-testid="close-modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* User Basic Info */}
              <div className="mb-6">
                <h4 className="text-lg font-bold mb-3 flex items-center">
                  👤 User Information
                  {getStatusBadge(selectedUser.id_verification_status)}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 rounded-lg p-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{selectedUser.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Role</p>
                    <p className="font-medium capitalize">{selectedUser.role}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{selectedUser.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{selectedUser.location || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Registered</p>
                    <p className="font-medium">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* ID Verification Info */}
              <div className="mb-6">
                <h4 className="text-lg font-bold mb-3">🪪 ID Verification</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">ID Type</p>
                      <p className="font-medium">{ID_TYPE_LABELS[selectedUser.id_type] || selectedUser.id_type || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ID Number</p>
                      <p className="font-medium font-mono">{selectedUser.id_number || 'Not provided'}</p>
                    </div>
                  </div>

                  {/* ID Images */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Front of ID</p>
                      {selectedUser.id_front_image ? (
                        <button
                          type="button"
                          onClick={() => setViewerImage({ src: `${BACKEND_URL}${selectedUser.id_front_image}`, alt: 'Front of ID' })}
                          className="block"
                          data-testid="id-front-thumb"
                        >
                          <img 
                            src={`${BACKEND_URL}${selectedUser.id_front_image}`} 
                            alt="ID Front" 
                            className="max-h-48 rounded border shadow hover:shadow-lg transition cursor-zoom-in"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; }}
                          />
                        </button>
                      ) : (
                        <div className="h-48 bg-gray-200 rounded flex items-center justify-center text-gray-500">
                          No image uploaded
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Back of ID</p>
                      {selectedUser.id_back_image ? (
                        <button
                          type="button"
                          onClick={() => setViewerImage({ src: `${BACKEND_URL}${selectedUser.id_back_image}`, alt: 'Back of ID' })}
                          className="block"
                          data-testid="id-back-thumb"
                        >
                          <img 
                            src={`${BACKEND_URL}${selectedUser.id_back_image}`} 
                            alt="ID Back" 
                            className="max-h-48 rounded border shadow hover:shadow-lg transition cursor-zoom-in"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; }}
                          />
                        </button>
                      ) : (
                        <div className="h-48 bg-gray-200 rounded flex items-center justify-center text-gray-500">
                          No image uploaded
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selfie and Barangay Clearance */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Selfie with ID</p>
                      {selectedUser.selfie_with_id ? (
                        <button
                          type="button"
                          onClick={() => setViewerImage({ src: `${BACKEND_URL}${selectedUser.selfie_with_id}`, alt: 'Selfie with ID' })}
                          className="block"
                          data-testid="selfie-thumb"
                        >
                          <img 
                            src={`${BACKEND_URL}${selectedUser.selfie_with_id}`} 
                            alt="Selfie with ID" 
                            className="max-h-48 rounded border shadow hover:shadow-lg transition cursor-zoom-in"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; }}
                          />
                        </button>
                      ) : (
                        <div className="h-48 bg-gray-200 rounded flex items-center justify-center text-gray-500">
                          No selfie uploaded
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Barangay Clearance</p>
                      {selectedUser.police_clearance ? (
                        <button
                          type="button"
                          onClick={() => setViewerImage({ src: `${BACKEND_URL}${selectedUser.police_clearance}`, alt: 'Barangay Clearance' })}
                          className="block"
                          data-testid="clearance-thumb"
                        >
                          <img 
                            src={`${BACKEND_URL}${selectedUser.police_clearance}`} 
                            alt="Barangay Clearance" 
                            className="max-h-48 rounded border shadow hover:shadow-lg transition cursor-zoom-in"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; }}
                          />
                        </button>
                      ) : (
                        <div className="h-48 bg-gray-200 rounded flex items-center justify-center text-gray-500">
                          No clearance uploaded
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rejection Reason (if rejected) */}
                  {selectedUser.id_verification_status === 'rejected' && selectedUser.id_rejection_reason && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-600 font-semibold">Rejection Reason:</p>
                      <p className="text-red-700">{selectedUser.id_rejection_reason}</p>
                    </div>
                  )}

                  {/* Stored rejection reasons history */}
                  {Array.isArray(selectedUser.kyc_rejection_reasons) && selectedUser.kyc_rejection_reasons.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded" data-testid="kyc-rejection-history">
                      <p className="text-sm text-red-600 font-semibold mb-1">Rejection Reasons:</p>
                      <ul className="text-red-700 text-sm list-disc list-inside space-y-1">
                        {selectedUser.kyc_rejection_reasons.map((r, idx) => (
                          <li key={idx}>{typeof r === 'string' ? r : (r?.reason || JSON.stringify(r))}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* AI Analysis Panel */}
                  {selectedUser.kyc_verification_result && typeof selectedUser.kyc_verification_result === 'object' && (
                    <details className="mt-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="ai-analysis-panel">
                      <summary className="cursor-pointer p-3 font-semibold text-blue-800 flex items-center gap-2">
                        🤖 AI Analysis Details
                        {typeof selectedUser.kyc_verification_result.overall_score === 'number' && (
                          <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-blue-200 text-blue-900">
                            Score: {selectedUser.kyc_verification_result.overall_score}/100 • {selectedUser.kyc_verification_result.decision || '—'}
                          </span>
                        )}
                      </summary>
                      <div className="p-4 pt-0 space-y-3 text-sm">
                        {selectedUser.kyc_verification_result.user_friendly_reason && (
                          <div className="bg-white border border-blue-100 rounded p-3">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">User-friendly reason</p>
                            <p className="text-gray-800">{selectedUser.kyc_verification_result.user_friendly_reason}</p>
                          </div>
                        )}

                        {selectedUser.kyc_verification_result.id_document_analysis && (
                          <div className="bg-white border border-blue-100 rounded p-3">
                            <p className="font-semibold text-gray-800 mb-2">📄 ID Document</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
                              <span>Valid Government ID:</span>
                              <span className="font-medium">{String(selectedUser.kyc_verification_result.id_document_analysis.is_valid_government_id)}</span>
                              <span>Name on ID:</span>
                              <span className="font-medium">{selectedUser.kyc_verification_result.id_document_analysis.name_on_id || '—'}</span>
                              <span>Name matches:</span>
                              <span className="font-medium">{String(selectedUser.kyc_verification_result.id_document_analysis.name_matches)}</span>
                              <span>Document quality:</span>
                              <span className="font-medium">{selectedUser.kyc_verification_result.id_document_analysis.document_quality || '—'}</span>
                              <span>Signs of tampering:</span>
                              <span className="font-medium">{String(selectedUser.kyc_verification_result.id_document_analysis.signs_of_tampering)}</span>
                              <span>Sub-score:</span>
                              <span className="font-medium">{selectedUser.kyc_verification_result.id_document_analysis.confidence_score ?? '—'}</span>
                            </div>
                          </div>
                        )}

                        {selectedUser.kyc_verification_result.face_matching_analysis && (
                          <div className="bg-white border border-blue-100 rounded p-3">
                            <p className="font-semibold text-gray-800 mb-2">👤 Face Matching</p>
                            <div className="space-y-1 text-gray-700">
                              <p><span className="text-gray-500">Face on ID:</span> {selectedUser.kyc_verification_result.face_matching_analysis.id_face_description || '—'}</p>
                              <p><span className="text-gray-500">Face in selfie:</span> {selectedUser.kyc_verification_result.face_matching_analysis.selfie_face_description || '—'}</p>
                              <p><span className="text-gray-500">Reasoning:</span> {selectedUser.kyc_verification_result.face_matching_analysis.match_reasoning || '—'}</p>
                              <div className="grid grid-cols-2 gap-x-4 mt-2">
                                <span>Faces match:</span>
                                <span className="font-medium">{String(selectedUser.kyc_verification_result.face_matching_analysis.faces_match)}</span>
                                <span>Holding ID in selfie:</span>
                                <span className="font-medium">{String(selectedUser.kyc_verification_result.face_matching_analysis.selfie_shows_person_holding_id)}</span>
                                <span>Sub-score:</span>
                                <span className="font-medium">{selectedUser.kyc_verification_result.face_matching_analysis.confidence_score ?? '—'}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedUser.kyc_verification_result.fraud_detection && (
                          <div className="bg-white border border-blue-100 rounded p-3">
                            <p className="font-semibold text-gray-800 mb-2">🛡️ Fraud Detection</p>
                            <div className="space-y-1 text-gray-700">
                              <p><span className="text-gray-500">Suspected fraud:</span> {String(selectedUser.kyc_verification_result.fraud_detection.suspected_fraud)}</p>
                              <p><span className="text-gray-500">Risk level:</span> {selectedUser.kyc_verification_result.fraud_detection.risk_level || '—'}</p>
                              {Array.isArray(selectedUser.kyc_verification_result.fraud_detection.fraud_indicators) && selectedUser.kyc_verification_result.fraud_detection.fraud_indicators.length > 0 && (
                                <div>
                                  <span className="text-gray-500">Indicators:</span>
                                  <ul className="list-disc list-inside ml-2">
                                    {selectedUser.kyc_verification_result.fraud_detection.fraud_indicators.map((ind, i) => (<li key={i}>{ind}</li>))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {selectedUser.kyc_verification_result.recommendations && (
                          <div className="bg-white border border-blue-100 rounded p-3">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Recommendations for user</p>
                            <p className="text-gray-800">{selectedUser.kyc_verification_result.recommendations}</p>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </div>

              {/* Payment Method Info */}
              <div className="mb-6">
                <h4 className="text-lg font-bold mb-3">💳 Payment Method</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Method</p>
                      {selectedUser.payment_method_type && PAYMENT_METHOD_CONFIG[selectedUser.payment_method_type] ? (
                        <p className="font-medium flex items-center gap-2">
                          <img src={PAYMENT_METHOD_CONFIG[selectedUser.payment_method_type].icon} alt="" className="w-6 h-6 object-contain rounded" />
                          {PAYMENT_METHOD_CONFIG[selectedUser.payment_method_type].label}
                        </p>
                      ) : (
                        <p className="font-medium">{selectedUser.payment_method_type || 'Not provided'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Account Name</p>
                      <p className="font-medium">{selectedUser.payment_account_name || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Account Number</p>
                      <p className="font-medium font-mono">{selectedUser.payment_account_number || 'Not provided'}</p>
                    </div>
                    {selectedUser.payment_method_type === 'bank_account' && (
                      <div>
                        <p className="text-sm text-gray-500">Bank Name</p>
                        <p className="font-medium">{selectedUser.payment_bank_name || 'Not provided'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons (for pending verifications) */}
              {(selectedUser.id_verification_status === 'pending' || selectedUser.id_verification_status === 'pending_admin_review' || selectedUser.id_verification_status === 'needs_review') && (
                <div className="border-t pt-6">
                  <h4 className="text-lg font-bold mb-3">⚡ Actions</h4>

                  {/* Inline AI reason so admins can decide without expanding the panel */}
                  {getAiReason(selectedUser) && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="ai-reason-inline">
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">🤖 Why this needs review</p>
                      <p className="text-sm text-amber-900">{getAiReason(selectedUser)}</p>
                    </div>
                  )}

                  <div className="flex gap-4 mb-4">
                    <button
                      onClick={() => handleApprove(selectedUser.id)}
                      disabled={processing}
                      className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 transition"
                      data-testid="approve-button"
                    >
                      {processing ? 'Processing...' : '✓ Approve Verification'}
                    </button>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <label className="block text-sm font-semibold text-red-700 mb-2">
                      Reject with Reason:
                    </label>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Enter reason for rejection (e.g., ID image unclear, information mismatch)"
                      rows="2"
                      className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-3"
                      data-testid="reject-reason-input"
                    />
                    <button
                      onClick={() => handleReject(selectedUser.id)}
                      disabled={processing || !rejectReason.trim()}
                      className="w-full bg-red-600 text-white py-2 px-6 rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50 transition"
                      data-testid="reject-button"
                    >
                      {processing ? 'Processing...' : '✗ Reject Verification'}
                    </button>
                  </div>
                </div>
              )}

              {/* Re-verify option for rejected users */}
              {selectedUser.id_verification_status === 'rejected' && (
                <div className="border-t pt-6">
                  <button
                    onClick={() => handleApprove(selectedUser.id)}
                    disabled={processing}
                    className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 transition"
                    data-testid="re-approve-button"
                  >
                    {processing ? 'Processing...' : '✓ Re-verify and Approve'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!embedded && <Footer />}

      <ImageZoomViewer
        open={!!viewerImage}
        src={viewerImage?.src}
        alt={viewerImage?.alt}
        onClose={() => setViewerImage(null)}
      />
    </div>
  );
};

export default AdminIDVerification;
